/**
 * Status pills — a pill-size Surface with a family-aware tone mapping.
 *
 * ## Why the mapping is a literal record and lives here
 *
 * `components.md` gives four families (product · domain · prefix · plan gate),
 * each mapping its values onto tones. Written as prose that mapping is a rule
 * nobody can check; written as a record it is a table a reader can audit and a
 * grep can find. The literal form is also what keeps `check:design-tokens`
 * honest — an interpolated `tone` would defeat the `tone="recall"` confinement
 * silently.
 *
 * ## Three places the design's own specimen is NOT what gets built
 *
 * **1 · Prefix pending and disputed.** The specimen shows `pending → warn` and
 * `disputed → error`, and `components.md` says to build the specimen *"until
 * that's ruled on"*. **It was ruled on** — C10, in the Build Plan Rulings:
 * pending is **neutral** and disputed is **warn**. Pending means a document is
 * in our queue and nothing is on the user's desk, so `warn` would be telling
 * them to act on something they cannot act on; a disputed prefix is usually a
 * licence in a founder's name or an acquisition, which exists to be resolved
 * rather than to report a fault. Building the ruling rather than the mock is
 * the instruction; encoding it here is what stops the ruling living only in
 * prose.
 *
 * **2 · There is no `pending` tone.** The domain family reads *"Waiting for DNS
 * → pending/inert · Verifying → pending"*, and `SURFACE_TONES` has six values,
 * none of them `pending`. The design means a *quiet* pill wearing
 * `--pp-pending` as its text colour, not a seventh tone — and the frozen
 * vocabulary is not widened for it.
 *
 * **3 · …and that pill sits on the surface, not on inert.** The specimen paints
 * `--pp-pending` (#63706E) on `--pp-inert` (#ECEFEF), which measures
 * **4.46:1** — under AA, at 11px, by four hundredths. On `--pp-surface` the
 * same grey is **5.16:1**. So `pending` renders as a `neutral` pill with the
 * pending foreground. This is the same family of defect as `--pp-live` used as
 * text: a status colour placed without a `-fg` token to carry it, and
 * `--pp-pending` has no `-fg` variant at all.
 */
import { createElement } from "react";
import { Surface } from "../surface/Surface.js";
/**
 * Every pill value in the product, mapped to a tone.
 *
 * A literal record, deliberately — see the header. Adding a family means adding
 * a key here, which is a diff a reviewer can read.
 */
export const PILL_TONES = {
    product: {
        published: "live",
        draft: "inert",
        archived: "inert",
        /** The terminal state. The copy is the shopper's words, not ours. */
        unavailable: "inert",
    },
    domain: {
        live: "live",
        waiting: "inert",
        /** Quiet, not warning: nothing is on the user's desk. */
        verifying: "neutral",
        error: "error",
    },
    prefix: {
        unverified: "inert",
        /** C10: neutral, NOT the specimen's warn. */
        pending: "neutral",
        verified: "live",
        /** C10: warn, NOT the specimen's error. */
        disputed: "warn",
    },
    /** Plan gates are flat neutral, border only — a label, never an alarm. */
    plan: {
        starter: "neutral",
        growth: "neutral",
        scale: "neutral",
    },
};
/**
 * Values whose foreground is `--pp-pending` rather than their tone's own.
 *
 * Two pills are "quiet but not dormant" — work is happening and none of it is
 * the user's. They read as neutral so they do not compete for attention, and
 * carry the pending grey so they do not read as finished.
 */
const PENDING_FOREGROUND = new Set(["verifying", "pending"]);
export function Pill({ family, value, children, className, }) {
    const tone = PILL_TONES[family][value];
    const pending = PENDING_FOREGROUND.has(String(value));
    return createElement(Surface, {
        as: "span",
        tone,
        emphasis: tone === "neutral" ? "flat" : "filled",
        size: "pill",
        className: [pending ? "pp-pill-pending" : null, className]
            .filter(Boolean)
            .join(" "),
        "data-pill-family": family,
        "data-pill-value": String(value),
    }, children);
}
//# sourceMappingURL=Pill.js.map